import {FC, useContext} from 'react';
import {observer} from 'mobx-react';
import {forEach, includes, isEmpty, get} from 'lodash';

import PlainInputSearchBox from '../../components/PlainInputSearchBox';
import {STAGE_DATA_SOURCE} from '../consts';
import {
  convertKeyType, convertValueType,
  getStageDataSchema, getTelemetryServiceSchema, getStageRenderingStrategy,
} from '../stageUtils';
import {Probe, ProbeProcessor, ProbeStage, ProcessorDefinition, StageDataSource} from '../types';
import humanizeString from '../../humanizeString';
import IBAContext from '../IBAContext';

type StageSearchBoxProps = {
  schema?: any[];
  filters?: any[];
  disabled?: boolean;
  onChange?: (value: any) => void;
  applyOnChange?: (value: any) => void;
  highlightPropertyColumn?: boolean;
  'aria-label'?: string;
  probe: Probe;
  processor: ProbeProcessor;
  stage: ProbeStage;
  stageItems?: any[];
  dataSource: StageDataSource;
  asAccordion?: boolean;
};

const StageSearchBox: FC<StageSearchBoxProps> = ({
  schema, filters, disabled, onChange, applyOnChange, highlightPropertyColumn, 'aria-label': ariaLabel,
  probe, processor, stage, stageItems, dataSource, asAccordion = true
}) => {
  const {processorDefinitions, isFreeform} = useContext(IBAContext);

  return (
    <PlainInputSearchBox
      filters={filters}
      schema={
        schema ||
        getStageSearchSchema({processorDefinitions, probe, processor, stage, stageItems, dataSource, isFreeform})
      }
      applyOnChange={applyOnChange}
      disabled={disabled}
      onChange={onChange}
      highlightPropertyColumn={highlightPropertyColumn}
      aria-label={ariaLabel}
      asAccordion={asAccordion}
      splitIntoColumns={2}
    />
  );
};

export default observer(StageSearchBox);

export const getStageSearchSchema = ({
  processorDefinitions, probe, processor, stage, stageItems = [], dataSource, isFreeform
}: {
  processorDefinitions: ProcessorDefinition[];
  probe: Probe;
  processor: ProbeProcessor;
  stage: ProbeStage;
  stageItems?: any[];
  dataSource: StageDataSource;
  isFreeform: boolean;
}) => {
  const isTelemetryServiceWarningsDataSource = dataSource === STAGE_DATA_SOURCE.telemetry_service_warnings;
  const renderingStrategy = getStageRenderingStrategy({stage, dataSource});
  const stageDataSchema = (
    isTelemetryServiceWarningsDataSource ?
      getTelemetryServiceSchema(stageItems) :
      getStageDataSchema({probe, stage, processor, renderingStrategy, processorDefinitions, showValue: false})
  )
    .filter(({name}) => !includes(['anomaly', 'warning', 'timestamp'], name))
    .reduce((result, {name, label, type: rawType}) => {
      const type = convertKeyType(rawType);
      result.push({
        name,
        schema: {
          title: label,
          type,
        },
        ...(type === 'number' ? {match: 'range', strictMode: true} : null),
        ...(type === 'string' ? {match: 'advancedString'} : null),
      });
      if (name === 'properties.system_id' && !isTelemetryServiceWarningsDataSource) {
        result.push({
          name: 'properties.system_hostname',
          schema: {type: 'string', title: 'System Hostname'},
          match: 'advancedString',
        });
        if (!isFreeform) {
          result.push({
            name: 'properties.system_role',
            schema: {type: 'string', title: 'System Role'},
            match: 'advancedString',
          });
        }
      }
      return result;
    }, []);

  if (!isTelemetryServiceWarningsDataSource) {
    forEach(stage?.values, ({name, title, type: rawType, possible_values: possibleValues, description}) => {
      if (rawType) {
        const type = convertValueType(rawType);
        const isDiscreteState = (type === 'string' && !isEmpty(possibleValues));
        stageDataSchema.push({
          name,
          schema: {
            title: title ?? humanizeString(name),
            type,
            description,
            ...(isDiscreteState ? {enum: possibleValues} : null),
          },
          ...(type === 'number' ? {match: 'range', strictMode: true} : null),
          ...(!isDiscreteState && type === 'string' ? {match: 'advancedString'} : null),
          units: get(stage, ['units', name]),
        });
      }
    });
  }

  return stageDataSchema;
};
